import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnifi, type UnifiConsole, type UnifiDevice, type UnifiSite, type UnifiWan } from '@/api/network';
import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { Donut } from '@/components/dashboard/charts';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { HBar, count, oneHue, toned } from '@/components/rmm/RmmDashboardKit';
import {
  Chip, CompanyCell, Empty, ListPage, PageLoading, Panel, Th, UnmappedNotice, tableWrap, theadCls, useListState, type FilterDef,
} from '@/components/network/NetworkKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, networkView } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Network → UniFi. Everything here is UniFi Site Manager data: console health and lifecycle
 * (UniFi OS, auto-update, config backups), sites and their clients, devices by product line, and
 * ISP/WAN quality from UniFi's hourly ISP metrics. Kept separate from Sophos on purpose.
 */

const U = ROUTES.network.unifi;
const DAY = 86_400_000;
const backupAge = (iso: string | null) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / DAY) : null);

// =================== Dashboard ===================

export function UnifiDashboardPage() {
  const { data: res, isLoading, error, isFetching } = useUnifi({ scoped: true });
  const navigate = useNavigate();
  const d = res?.data;
  const k = d?.kpis;

  const deviceStatus = k ? [
    { id: 'online', label: 'Online', value: k.devicesOnline, tone: 'success' as const },
    { id: 'offline', label: 'Offline', value: k.devicesOffline, tone: 'destructive' as const },
    { id: 'unknown', label: 'Unknown', value: k.devicesUnknown, tone: 'neutral' as const },
  ].filter((b) => b.value > 0) : [];

  const worstWan = (d?.wan ?? []).filter((w) => !w.noData).slice(0, 8);
  const consoleAttention = (d?.consoles ?? []).filter((c) => !c.connected || c.firmwareUpdate || (c.kind === 'console' && (backupAge(c.lastBackup) ?? 999) > 30))
    .sort((a, b) => Number(a.connected) - Number(b.connected));

  return (
    <DesktopOnly title="Network is available on desktop" message="Open Cosmos on a larger screen to view the network.">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
        <PageHeader title="UniFi" isFetching={isFetching} subtitle={d ? <>UniFi Site Manager · read {fmtRelative(d.fetchedAt)}</> : 'UniFi Site Manager'} />
        <PageLoading isLoading={isLoading} error={error} configured={res?.configured} vendor="UniFi" />
        {d && k && (
          <>
            <div className="mt-3"><UnmappedNotice count={k.unmapped} what={`UniFi site${k.unmapped === 1 ? '' : 's'}`} /></div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 min-[1800px]:grid-cols-8">
              <StatCard label="Consoles" value={k.consoles} sub={`${k.consolesDisconnected} disconnected`} tone={k.consolesDisconnected ? 'warning' : 'success'} onClick={() => navigate(networkView(U.consoles, { show: 'disconnected' }))} />
              <StatCard label="Devices offline" value={k.devicesOffline} sub={`of ${k.devices} · ${k.devicesUnknown} unknown`} tone={k.devicesOffline ? 'destructive' : 'success'} onClick={() => navigate(networkView(U.devices, { show: 'offline' }))} />
              <StatCard label="Firmware updates" value={k.deviceUpdates + k.consoleUpdates} sub={`${k.deviceUpdates} device${k.deviceUpdates === 1 ? '' : 's'} · ${k.consoleUpdates} console${k.consoleUpdates === 1 ? '' : 's'}`} tone={k.deviceUpdates + k.consoleUpdates ? 'warning' : 'success'} onClick={() => navigate(networkView(U.devices, { show: 'updates' }))} />
              <StatCard label="Stale config backup" value={k.staleBackups} sub="no backup in 30+ days" tone={k.staleBackups ? 'warning' : 'success'} onClick={() => navigate(networkView(U.consoles, { show: 'backup' }))} />
              <StatCard label="Auto-update off" value={k.noAutoUpdate} sub="consoles" tone={k.noAutoUpdate ? 'warning' : 'success'} onClick={() => navigate(networkView(U.consoles, { show: 'autoupdate' }))} />
              <StatCard label="WAN degraded" value={k.wanDegraded} sub={`of ${k.wanSites} gateways, 7 days`} tone={k.wanDegraded ? 'warning' : 'success'} onClick={() => navigate(U.wan)} />
              <StatCard label="Internet issues" value={k.sitesWithWanIssues} sub="sites reporting issues" tone={k.sitesWithWanIssues ? 'warning' : 'success'} onClick={() => navigate(networkView(U.sites, { show: 'wan' }))} />
              <StatCard label="Clients" value={k.clients} sub={`across ${k.sites} sites`} onClick={() => navigate(U.sites)} />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <ChartCard title="Device status">
                <Donut data={toned(deviceStatus)} valueFormatter={count} height={200} onSelect={(b) => navigate(networkView(U.devices, { show: b.id === 'online' ? 'all' : b.id === 'offline' ? 'offline' : 'behind' }))} />
              </ChartCard>
              <ChartCard title="Devices by product line">
                <HBar data={oneHue(d.charts.productLines)} name="Devices" onSelect={() => navigate(U.devices)} />
              </ChartCard>
              <ChartCard title="UniFi OS on consoles">
                <HBar data={oneHue(d.charts.unifiOs)} name="Consoles" onSelect={() => navigate(U.consoles)} />
              </ChartCard>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 min-[1400px]:grid-cols-2">
              <Panel title="Weakest WAN links · last 7 days" more={{ to: U.wan, label: 'ISP & WAN' }}>
                {worstWan.length === 0 ? <div className="px-4 py-6 text-center text-xs text-muted-foreground">No ISP metrics (only sites with a UniFi gateway report them).</div> : (
                  <table className="w-full text-sm"><tbody className="divide-y divide-divider">
                    {worstWan.map((w) => (
                      <tr key={w.siteId}>
                        <td className="px-4 py-2"><div className="whitespace-nowrap font-medium text-foreground">{w.label}</div><div className="whitespace-nowrap text-[11px] text-muted-foreground">{w.isp ?? 'ISP unknown'}</div></td>
                        <td className="px-4 py-2"><Uptime w={w} /></td>
                        <td className="whitespace-nowrap px-4 py-2 text-right text-xs tabular-nums text-muted-foreground"><div>{w.packetLossPct ?? '—'}% loss</div><div>{w.avgLatencyMs ?? '—'} ms</div></td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
              </Panel>
              <Panel title={`Consoles needing attention · ${consoleAttention.length}`} more={{ to: U.consoles, label: 'All consoles' }}>
                {consoleAttention.length === 0 ? <div className="px-4 py-6 text-center text-xs text-success">Every console connected, current and backed up.</div> : (
                  <table className="w-full text-sm"><tbody className="divide-y divide-divider">
                    {consoleAttention.slice(0, 20).map((c) => (
                      <tr key={c.id}>
                        <td className="px-4 py-2"><div className="whitespace-nowrap font-medium text-foreground">{c.name}</div><div className="whitespace-nowrap text-[11px] text-muted-foreground">{c.hardwareName ?? c.model ?? '—'}</div></td>
                        <td className="max-w-[160px] px-4 py-2 text-xs"><CompanyCell companyId={c.companyId} companyName={c.companyName} /></td>
                        <td className="px-4 py-2 text-right"><div className="flex flex-wrap justify-end gap-1">
                          {!c.connected && <Chip tone="bad">Disconnected</Chip>}
                          {c.firmwareUpdate && <Chip tone="info">→ {c.firmwareUpdate}</Chip>}
                          {c.kind === 'console' && (backupAge(c.lastBackup) ?? 999) > 30 && <Chip tone="warn">{c.lastBackup ? `Backup ${backupAge(c.lastBackup)}d old` : 'No backup'}</Chip>}
                        </div></td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
              </Panel>
            </div>
          </>
        )}
      </div>
    </DesktopOnly>
  );
}

function Uptime({ w }: { w: UnifiWan }) {
  if (w.noData) return <span className="text-xs text-muted-foreground">No data</span>;
  // Hourly uptime as a strip of cells (last 7 days); the % beside it carries the value.
  const series = w.series.slice(-168);
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-3 w-40 overflow-hidden rounded-sm bg-muted" title="Hourly WAN uptime, last 7 days">
        {series.map((p, i) => (
          <span key={i} className={cn('h-full flex-1', p.uptime == null ? 'bg-muted' : p.uptime >= 99.9 ? 'bg-success/70' : p.uptime >= 95 ? 'bg-warning' : 'bg-destructive')} />
        ))}
      </div>
      <span className={cn('w-14 text-right text-xs tabular-nums', (w.uptimePct ?? 100) < 99.5 ? 'font-medium text-warning' : 'text-foreground')}>{w.uptimePct ?? '—'}%</span>
    </div>
  );
}

// =================== Sites ===================

const SITE_FILTERS: FilterDef<UnifiSite>[] = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'disconnected', label: 'Console disconnected', test: (s) => !s.hostConnected },
  { id: 'offline', label: 'Devices offline', test: (s) => s.hostConnected && s.offline > 0 },
  { id: 'wan', label: 'Internet issues', test: (s) => s.internetIssues > 0 },
  { id: 'updates', label: 'Updates pending', test: (s) => s.pendingUpdates > 0 },
  { id: 'unmapped', label: 'Unmapped', test: (s) => s.companyId == null && !s.mappingIgnored },
];

export function UnifiSitesPage() {
  const { data: res, isLoading, error, isFetching } = useUnifi({ scoped: true });
  const d = res?.data;
  const st = useListState('cosmos:network:unifi:sites', SITE_FILTERS, d?.sites ?? [], (s) => `${s.label} ${s.companyName ?? ''} ${s.wanIp ?? ''} ${s.isp ?? ''} ${s.hostModel ?? ''}`);
  return (
    <ListPage title="UniFi sites" subtitle="One row per site (a console usually holds one). Stats on a disconnected console are its last-known values."
      count={d ? st.rows.length : null} isFetching={isFetching} search={{ q: st.q, setQ: st.setQ, placeholder: 'Console, client, IP or ISP' }}
      filters={{ defs: SITE_FILTERS, show: st.show, pick: st.pick, counts: st.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={res?.configured} vendor="UniFi" />
      {d && (
        <div className={tableWrap}>
          <table className="w-full text-sm" style={{ minWidth: 1100 }}>
            <thead className={theadCls}><tr><Th>Site</Th><Th>Client</Th><Th>Console</Th><Th right>Devices</Th><Th right>Offline</Th><Th right>Wi-Fi</Th><Th right>Wired</Th><Th>Gateway / WAN</Th><Th right>Wi-Fi retries</Th><Th right>Updates</Th></tr></thead>
            <tbody className="divide-y divide-divider">
              {st.rows.length === 0 && <Empty cols={10} />}
              {st.rows.map((s) => (
                <tr key={s.id} className="align-top hover:bg-muted/30">
                  <td className="px-3 py-2"><div className="max-w-[220px] truncate font-medium text-foreground" title={s.label}>{s.label}</div><div className="text-[11px] text-muted-foreground">{s.hostModel ?? '—'}</div></td>
                  <td className="max-w-[180px] px-3 py-2 text-xs">{s.mappingIgnored ? <span className="italic text-muted-foreground">ignored</span> : <CompanyCell companyId={s.companyId} companyName={s.companyName} />}</td>
                  <td className="px-3 py-2">{s.hostConnected ? <Chip tone="good">Connected</Chip> : <Chip tone="bad">Disconnected</Chip>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.devices}</td>
                  <td className={cn('px-3 py-2 text-right tabular-nums', s.offline ? 'font-medium text-warning' : 'text-muted-foreground')}>{s.offline || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{s.wifiClients}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{s.wiredClients}</td>
                  <td className="min-w-[220px] px-3 py-2 text-xs">
                    {s.gateway ? (
                      <>
                        <span className="text-foreground">{s.gateway}</span>{s.ips && s.ips !== 'disabled' && <span className="text-muted-foreground"> · IPS {s.ips}</span>}
                        <div className="text-[11px] text-muted-foreground">{s.wanIp ?? '—'}{s.isp && ` · ${s.isp}`}{s.wanUptimePct != null && ` · ${Math.round(s.wanUptimePct * 10) / 10}% up`}{s.internetIssues ? ` · ${s.internetIssues} issue${s.internetIssues === 1 ? '' : 's'}` : ''}</div>
                      </>
                    ) : <span className="text-muted-foreground">No UniFi gateway</span>}
                  </td>
                  <td className={cn('px-3 py-2 text-right text-xs tabular-nums', (s.txRetryPct ?? 0) > 15 ? 'text-warning' : 'text-muted-foreground')} title="Wi-Fi transmit retries — high values mean interference or poor coverage">{s.txRetryPct != null ? `${s.txRetryPct}%` : '—'}</td>
                  <td className={cn('px-3 py-2 text-right tabular-nums', s.pendingUpdates ? 'text-info' : 'text-muted-foreground')}>{s.pendingUpdates || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}

// =================== Consoles ===================

const CONSOLE_FILTERS: FilterDef<UnifiConsole>[] = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'disconnected', label: 'Disconnected', test: (c) => !c.connected },
  { id: 'updates', label: 'UniFi OS update', test: (c) => !!c.firmwareUpdate },
  { id: 'backup', label: 'Backup 30+ days / none', test: (c) => c.kind === 'console' && (backupAge(c.lastBackup) ?? 999) > 30 },
  { id: 'autoupdate', label: 'Auto-update off', test: (c) => c.kind === 'console' && !c.autoUpdate },
  { id: 'unmapped', label: 'Unmapped', test: (c) => c.companyId == null && c.sites <= 1 },
];

export function UnifiConsolesPage() {
  const { data: res, isLoading, error, isFetching } = useUnifi({ scoped: true });
  const d = res?.data;
  const st = useListState('cosmos:network:unifi:consoles', CONSOLE_FILTERS, d?.consoles ?? [], (c) => `${c.name} ${c.hardwareName ?? ''} ${c.model ?? ''} ${c.publicIp ?? ''} ${c.companyName ?? ''}`);
  return (
    <ListPage title="UniFi consoles" subtitle="Gateways, Cloud Keys and the self-hosted Network server — UniFi OS, Network app, auto-update and config backups"
      count={d ? st.rows.length : null} isFetching={isFetching} search={{ q: st.q, setQ: st.setQ, placeholder: 'Console, model, IP or client' }}
      filters={{ defs: CONSOLE_FILTERS, show: st.show, pick: st.pick, counts: st.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={res?.configured} vendor="UniFi" />
      {d && (
        <div className={tableWrap}>
          <table className="w-full text-sm" style={{ minWidth: 1100 }}>
            <thead className={theadCls}><tr><Th>Console</Th><Th>Client</Th><Th>Status</Th><Th>UniFi OS</Th><Th>Network app</Th><Th>Auto-update</Th><Th>Last backup</Th><Th>Public IP</Th></tr></thead>
            <tbody className="divide-y divide-divider">
              {st.rows.length === 0 && <Empty cols={8} />}
              {st.rows.map((c) => {
                const age = backupAge(c.lastBackup);
                return (
                  <tr key={c.id} className="align-top hover:bg-muted/30">
                    <td className="px-3 py-2"><div className="max-w-[240px] truncate font-medium text-foreground" title={c.name}>{c.name}</div><div className="whitespace-nowrap text-[11px] text-muted-foreground">{c.hardwareName ?? c.model ?? '—'}{c.sites > 1 && ` · ${c.sites} sites`}{!c.owner && ' · shared with Synfo'}</div></td>
                    <td className="max-w-[200px] px-3 py-2 text-xs">{c.sites > 1 ? <span className="text-muted-foreground">several clients</span> : <CompanyCell companyId={c.companyId} companyName={c.companyName} />}</td>
                    <td className="px-3 py-2">{c.connected ? <Chip tone="good">Connected</Chip> : <Chip tone="bad" title={c.stateChangedAt ? `Since ${c.stateChangedAt}` : undefined}>Disconnected</Chip>}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums">{c.unifiOs ?? '—'}{c.firmwareUpdate && <div><Chip tone="info">→ {c.firmwareUpdate}</Chip></div>}</td>
                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{c.networkApp ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">{c.kind !== 'console' ? <span className="text-muted-foreground">n/a</span> : c.autoUpdate ? <span className="text-success">On{c.releaseChannel && c.releaseChannel !== 'release' ? ` (${c.releaseChannel})` : ''}</span> : <span className="text-warning">Off</span>}</td>
                    <td className={cn('whitespace-nowrap px-3 py-2 text-xs', c.kind === 'console' && (age ?? 999) > 30 ? 'text-warning' : 'text-muted-foreground')}>{c.lastBackup ? `${age}d ago` : c.kind === 'console' ? 'never' : '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-muted-foreground">{c.publicIp ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}

// =================== Devices ===================

const DEV_FILTERS: FilterDef<UnifiDevice>[] = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'offline', label: 'Offline', test: (d) => !d.online && d.hostConnected },
  { id: 'updates', label: 'Update available', test: (d) => d.updateAvailable },
  { id: 'behind', label: 'Console disconnected', test: (d) => !d.hostConnected },
  { id: 'network', label: 'Network', test: (d) => d.productLine === 'network' },
  { id: 'protect', label: 'Protect', test: (d) => d.productLine === 'protect' },
  { id: 'access', label: 'Access', test: (d) => d.productLine === 'access' },
];

export function UnifiDevicesPage() {
  const { data: res, isLoading, error, isFetching } = useUnifi({ scoped: true });
  const d = res?.data;
  const st = useListState('cosmos:network:unifi:devices', DEV_FILTERS, d?.devices ?? [], (x) => `${x.name} ${x.model ?? ''} ${x.ip ?? ''} ${x.mac ?? ''} ${x.hostName} ${x.companyName ?? ''}`);
  return (
    <ListPage title="UniFi devices" subtitle="Switches, access points, gateways and Protect cameras. Devices on the self-hosted Network server aren't visible to the Site Manager API."
      count={d ? st.rows.length : null} isFetching={isFetching} search={{ q: st.q, setQ: st.setQ, placeholder: 'Name, model, IP, MAC or client' }}
      filters={{ defs: DEV_FILTERS, show: st.show, pick: st.pick, counts: st.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={res?.configured} vendor="UniFi" />
      {d && (
        <div className={tableWrap}>
          <table className="w-full text-sm" style={{ minWidth: 1050 }}>
            <thead className={theadCls}><tr><Th>Device</Th><Th>Client</Th><Th>Console</Th><Th>Status</Th><Th>Firmware</Th><Th>Up since</Th><Th>IP / MAC</Th></tr></thead>
            <tbody className="divide-y divide-divider">
              {st.rows.length === 0 && <Empty cols={7} />}
              {st.rows.slice(0, 500).map((x) => (
                <tr key={`${x.hostId}:${x.id}`} className="hover:bg-muted/30">
                  <td className="px-3 py-2"><div className="max-w-[220px] truncate font-medium text-foreground" title={x.name}>{x.name}</div><div className="whitespace-nowrap text-[11px] capitalize text-muted-foreground">{x.model ?? '—'}{x.productLine && ` · ${x.productLine}`}</div></td>
                  <td className="max-w-[200px] px-3 py-2 text-xs"><CompanyCell companyId={x.companyId} companyName={x.companyName} /></td>
                  <td className="max-w-[180px] truncate px-3 py-2 text-xs text-muted-foreground" title={x.hostName}>{x.hostName || '—'}</td>
                  <td className="px-3 py-2">{x.online ? <Chip tone="good">Online</Chip> : x.hostConnected ? <Chip tone="bad">Offline</Chip> : <Chip tone="muted" title="The console is disconnected, so this device's state is unknown">Unknown</Chip>}</td>
                  <td className="px-3 py-2 text-xs tabular-nums"><div className="max-w-[150px] truncate" title={x.version ?? ''}>{x.version ?? '—'}</div>{x.updateAvailable && <div><Chip tone="info">Update available</Chip></div>}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{x.online && x.startupTime ? fmtRelative(x.startupTime).replace(/^(about|almost|over) /, '') : '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-muted-foreground">{x.ip ?? '—'}<div className="text-[11px]">{x.mac}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}

// =================== ISP & WAN ===================

const WAN_FILTERS: FilterDef<UnifiWan>[] = [
  { id: 'all', label: 'All gateways', test: () => true },
  { id: 'degraded', label: 'Degraded (< 99.5 % up or > 1 % loss)', test: (w) => !w.noData && ((w.uptimePct ?? 100) < 99.5 || (w.packetLossPct ?? 0) > 1) },
  { id: 'nodata', label: 'No data', test: (w) => w.noData },
];

export function UnifiWanPage() {
  const { data: res, isLoading, error, isFetching } = useUnifi({ scoped: true });
  const d = res?.data;
  const st = useListState('cosmos:network:unifi:wan', WAN_FILTERS, d?.wan ?? [], (w) => `${w.label} ${w.isp ?? ''} ${w.companyName ?? ''}`);
  const isps = useMemo(() => d?.charts.isps ?? [], [d]);
  return (
    <ListPage title="ISP & WAN" subtitle="UniFi ISP metrics, hourly over the last 7 days — only sites with a UniFi gateway report them"
      count={d ? st.rows.length : null} isFetching={isFetching} search={{ q: st.q, setQ: st.setQ, placeholder: 'Site, ISP or client' }}
      filters={{ defs: WAN_FILTERS, show: st.show, pick: st.pick, counts: st.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={res?.configured} vendor="UniFi" />
      {d && (
        <>
          <div className={tableWrap}>
            <table className="w-full text-sm" style={{ minWidth: 1050 }}>
              <thead className={theadCls}><tr><Th>Site</Th><Th>Client</Th><Th>ISP</Th><Th>Uptime (7d)</Th><Th right>Downtime</Th><Th right>Packet loss</Th><Th right>Latency avg/max</Th><Th right>Down / up</Th></tr></thead>
              <tbody className="divide-y divide-divider">
                {st.rows.length === 0 && <Empty cols={8} />}
                {st.rows.map((w) => (
                  <tr key={w.siteId} className="hover:bg-muted/30">
                    <td className="px-3 py-2 font-medium text-foreground"><div className="max-w-[160px] truncate" title={w.label}>{w.label}</div></td>
                    <td className="max-w-[140px] px-3 py-2 text-xs"><CompanyCell companyId={w.companyId} companyName={w.companyName} /></td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{w.isp ?? '—'}</td>
                    <td className="px-3 py-2"><Uptime w={w} /></td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">{w.noData ? '—' : w.downtimeMin ? `${w.downtimeMin} min` : '—'}</td>
                    <td className={cn('px-3 py-2 text-right text-xs tabular-nums', (w.packetLossPct ?? 0) > 1 ? 'font-medium text-warning' : 'text-muted-foreground')}>{w.packetLossPct != null ? `${w.packetLossPct}%` : '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">{w.avgLatencyMs != null ? `${w.avgLatencyMs} / ${w.maxLatencyMs ?? '—'} ms` : '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">{w.downloadMbps != null ? `${w.downloadMbps} / ${w.uploadMbps ?? '—'} Mbps` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isps.length > 0 && (
            <div className="mt-4 max-w-xl"><ChartCard title="Sites by ISP"><HBar data={oneHue(isps)} name="Sites" /></ChartCard></div>
          )}
        </>
      )}
    </ListPage>
  );
}
